import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  // Global axios interceptor: auto-refresh access token on 401, then retry once
  useEffect(() => {
    let refreshing = null;
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const original = err?.config || {};
        const status = err?.response?.status;
        const url = original?.url || '';
        // Skip retry for refresh or auth endpoints or already-retried requests
        if (
          status !== 401 ||
          original._retry ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/login') ||
          url.includes('/auth/register')
        ) {
          return Promise.reject(err);
        }
        original._retry = true;
        try {
          if (!refreshing) {
            refreshing = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
              .finally(() => { refreshing = null; });
          }
          await refreshing;
          // Retry original request with same config (cookies will carry new token)
          return axios({ ...original, withCredentials: true });
        } catch (refreshErr) {
          return Promise.reject(err);
        }
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent-Auth OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user && user !== false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
